import { useTheme } from "@mui/material";
import { ChartCard } from "@pages:app/analytics/components/cards/ChartCard";
import { DashboardGrid } from "@pages:app/analytics/components/cards/DashboardGrid";
import { DonutCard } from "@pages:app/analytics/components/cards/DonutCard";
import { StatCard } from "@pages:app/analytics/components/cards/StatCard";
import { AnalyticsData } from "@pages:app/analytics/tabs/useAnalyticsData";
import { useTranslation } from "react-i18next";
import { MdBarChart, MdInput, MdOutlinePeople, MdOutput } from "react-icons/md";

type GeneralTabProps = {
  data: AnalyticsData;
};

export const GeneralTab = ({ data }: GeneralTabProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const iconColor = theme.palette.primary.main;
  const {
    summaryQuery,
    tokenUsageQuery,
    requestsByFeatureQuery,
    activeUsersChart,
    requestsChart,
    tokenUsageChart,
    topUsersSlices,
    topUsersQuery,
    featureSlices,
    modelSlices,
    sumSlices,
    formatCompact,
  } = data;

  return (
    <DashboardGrid>
      {/* Row 1 */}
      <StatCard
        label={t("analytics.activeUsers")}
        value={summaryQuery.data?.activeUsersPeriodDistinct}
        icon={<MdOutlinePeople size={22} color={iconColor} />}
        colSpan={3}
      />
      <StatCard
        label={t("analytics.requests")}
        value={summaryQuery.data?.requestsPeriod}
        icon={<MdBarChart size={22} color={iconColor} />}
        colSpan={3}
      />
      <StatCard
        label={t("analytics.inputTokens")}
        value={tokenUsageQuery.data?.inputTokens}
        icon={<MdInput size={22} color={iconColor} />}
        colSpan={3}
        format={formatCompact}
      />
      <StatCard
        label={t("analytics.outputTokens")}
        value={tokenUsageQuery.data?.outputTokens}
        icon={<MdOutput size={22} color={iconColor} />}
        colSpan={3}
        format={formatCompact}
      />

      {/* Row 2 */}
      <ChartCard
        title={t("analytics.activeUsers")}
        xAxis={activeUsersChart.xAxis}
        yAxis={activeUsersChart.yAxis}
        series={activeUsersChart.series}
        colSpan={6}
        rowSpan={2}
        integerYTicks
      />
      <ChartCard
        title={t("analytics.numberOfRequests")}
        xAxis={requestsChart.xAxis}
        yAxis={requestsChart.yAxis}
        series={requestsChart.series}
        colSpan={6}
        rowSpan={2}
        integerYTicks
      />

      {/* Row 3 */}
      <DonutCard
        title={t("analytics.usersByUsage")}
        slices={topUsersSlices}
        centerValue={sumSlices(topUsersSlices)}
        centerLabel={t("analytics.unit.requests")}
        loading={topUsersQuery.isLoading}
        emptyText={t("analytics.empty.activity")}
        colSpan={4}
      />
      <DonutCard
        title={t("analytics.requestsByFeature")}
        slices={featureSlices}
        centerValue={sumSlices(featureSlices)}
        centerLabel={t("analytics.unit.requests")}
        loading={requestsByFeatureQuery.isLoading}
        emptyText={t("analytics.empty.requests")}
        colSpan={4}
      />
      <DonutCard
        title={t("analytics.tokenUsageByModel")}
        slices={modelSlices}
        centerValue={sumSlices(modelSlices)}
        centerLabel={t("analytics.unit.tokens")}
        loading={tokenUsageQuery.isLoading}
        emptyText={t("analytics.empty.tokenUsage")}
        colSpan={4}
        format={formatCompact}
      />

      {/* Row 4 */}
      <ChartCard
        title={t("analytics.tokenUsage")}
        xAxis={tokenUsageChart.xAxis}
        yAxis={tokenUsageChart.yAxis}
        series={tokenUsageChart.series}
        rowSpan={2}
        hideLegend={false}
        yScale="symlog"
        yValueFormatter={formatCompact}
      />
    </DashboardGrid>
  );
};
